(ns app.front.view.user-management.model
  "Pure helpers for the user management views of a tenant and of a single
  group: how a group's user-groups split into the editor's blocks, and the
  rules applied when a membership is toggled in a draft. A draft's groups are
  a set of user-group ids."
  (:require [app.domain.group.model :as group-model]
            [app.external-module.model :as external-model]
            [app.front.view.group.admin.common :as acommon]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.user.model :as user-model]
            [potpuri.core :as p]))

;; ---------------------------------------------------------------------------
;; Groups
;; ---------------------------------------------------------------------------

(defn plain-group?
  "A user-group that is neither a permission, a viewer nor an external module's."
  [group]
  (and (acommon/user-selectable-group? group)
       (nil? (:group/external-kind group))))

(defn classify-groups
  "Splits one group's user-groups into the lists the editor shows: permissions,
  viewer access and groups for a Cloudpermit group, and for an external module
  its roles (a single choice) and departments."
  [groups]
  (let [sorted #(->> groups (filter %) (sort-by :group/name) vec)]
    {:permissions          (sorted acommon/write-auth-group?)
     :viewer               (sorted acommon/viewer-group?)
     :plain                (sorted plain-group?)
     :external-roles       (sorted external-model/role-group?)
     :external-departments (sorted external-model/department-group?)}))

(defn- ids-of [pred groups]
  (->> groups (filter pred) (map :db/id) set))

(defn toggle-group
  "The group ids of a draft after `group-id` is checked or unchecked, with what
  that implies: a viewer group excludes every other group, except in a
  superdomain; a write group brings the write-auth group and, except in a
  superdomain, drops the viewer; unchecking org admin drops escrow admin; and
  an external module holds one role."
  [{:keys [groups superdomain?]} group-ids group-id checked?]
  (let [group (p/find-first groups {:db/id group-id})
        ids   ((if checked? conj disj) (set group-ids) group-id)]
    (cond
      (and checked? (external-model/role-group? group))
      (-> (set/difference ids (ids-of external-model/role-group? groups))
          (conj group-id))

      (and checked? (not superdomain?) (acommon/viewer-group? group))
      (set/difference ids (ids-of (complement acommon/viewer-group?) groups))

      (and checked? (acommon/write-group? group))
      (cond-> (set/union ids (ids-of acommon/write-auth-group? groups))
        (not superdomain?) (set/difference (ids-of acommon/viewer-group? groups)))

      (and (not checked?) (acommon/org-admin-group? group))
      (set/difference ids (ids-of acommon/escrow-admin-group? groups))

      :else
      ids)))

(defn group-disabled?
  "Whether a group's checkbox cannot be changed in the draft: admins keep their
  own admin group, a group a configuration relies on keeps its last member, a
  person keeps at least one group, and escrow admin needs org admin."
  [{:keys [own-admin-group-ids member-counts]} groups group-ids {:keys [db/id used-in-group-config?] :as group}]
  (let [checked?  (contains? group-ids id)
        selected  (filter (comp group-ids :db/id) groups)
        others    (remove #(or (= id (:db/id %)) (acommon/escrow-admin-group? %)) selected)
        org-admin (some acommon/org-admin-group? selected)]
    (if checked?
      (or (contains? own-admin-group-ids id)
          (and used-in-group-config? (< (get member-counts id 0) 2))
          (empty? others))
      (and (acommon/escrow-admin-group? group)
           (not org-admin)))))

;; ---------------------------------------------------------------------------
;; Users
;; ---------------------------------------------------------------------------

(def service-account? user-model/service-account?)

(defn review-assignment-group-names
  "The review assignment groups among the user's groups (as `:group/get-users`
  returns them, with roles), by name."
  [user]
  (->> (:user/groups user)
       (filter (partial group-model/has-role? :group.role/review-assignment-group))
       (map :group/name)
       sort
       seq))

(defn display-name [user]
  (let [n (->> [(:user/first-name user) (:user/last-name user)]
               (remove str/blank?)
               (str/join " "))]
    (if (str/blank? n)
      (:user/username user)
      n)))

(defn matches?
  "Whether the lower-cased `query` appears in the user's name, username, email
  or `group-names`."
  [query group-names user]
  (or (str/blank? query)
      (some #(some-> % str/lower-case (str/includes? query))
            (concat [(display-name user)
                     (:user/username user)
                     (:user/email user)]
                    group-names))))

(defn normalize-query [query]
  (some-> query str/trim str/lower-case))

(defn draft-of
  "The draft a user's editor starts from: their names and their memberships
  among `editable-ids`."
  [user editable-ids]
  {:first-name (:user/first-name user)
   :last-name  (:user/last-name user)
   :group-ids  (set/intersection (set (:user/group-ids user)) editable-ids)})

(defn changes
  "What saving `draft` changes compared to `original` (both from `draft-of`):
  the group ids to add and remove, and the names that differ."
  [original draft]
  (p/assoc-if {:add    (set/difference (:group-ids draft) (:group-ids original))
               :remove (set/difference (:group-ids original) (:group-ids draft))}
              :first-name (when (not= (:first-name original) (:first-name draft))
                            (:first-name draft))
              :last-name (when (not= (:last-name original) (:last-name draft))
                           (:last-name draft))))

(defn dirty? [{:keys [add remove first-name last-name]}]
  (or (seq add) (seq remove) first-name last-name))
